import React, { useCallback } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import Select from "react-select";
import customStyles from "../../customStyles";
import Spinner from "../../reusableComponents/spinner/Spinner";
import { alphaCreator } from "../../utils/apis/payments";
import { returnMessages } from "../../utils/store/reducers/Message";
import { returnErrors } from "../../utils/store/reducers/Error";
import { useDispatch, useSelector } from "react-redux";

const AlphaCreator = () => {
  const dispatch = useDispatch();
  const { idToken } = useSelector((state) => state.auth);

  const paymentOptions = [
    { label: "ACH", value: "ACH" },
    { label: "WIRE", value: "WIRE" },
    { label: "SWIFT", value: "SWIFT" },
    { label: "WISE", value: "WISE" },
    { label: "RISE", value: "RISE" },
  ];

  const platformOptions = [
    { label: "Instagram", value: "instagram" },
    { label: "Facebook", value: "facebook" },
    { label: "Twitter", value: "twitter" },
    { label: "TikTok", value: "tiktok" },
    { label: "YouTube", value: "youtube" },
  ];

  const validationSchema = Yup.object({
    method: Yup.string().required("Payment method is required"),
    platforms: Yup.array()
      .of(Yup.string())
      .min(1, "At least one platform is required"),
    meta: Yup.object().test(
      "meta-urls",
      "Valid HTTPS URLs are required for each platform",
      (meta, context) => {
        const { platforms } = context.parent;
        if (!platforms || !meta) return false;
        return platforms.every((platform) => {
          const url = meta[platform];
          return (
            url &&
            /^https:\/\/.+/.test(url) &&
            Yup.string().url().isValidSync(url)
          );
        });
      }
    ),
  });

  const formik = useFormik({
    initialValues: {
      method: "",
      platforms: [],
      meta: {},
    },
    validationSchema,
    validateOnChange: false,
    validateOnBlur: true,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        const payload = {
          method: values.method,
          meta: values.meta,
        };
        const response = await alphaCreator(idToken, payload);
        if (response.status === 200 || response.status === 201) {
          dispatch(returnMessages(response.data.detail, response.status));
          resetForm({ values: { method: "", platforms: [], meta: {} } });
        } else {
          throw new Error("Unexpected response status");
        }
      } catch (error) {
        const errorMessage =
          error.response?.data?.detail || "Something went wrong";
        dispatch(returnErrors(errorMessage, error.response?.status || 400));
        console.error("Submission failed:", error);
      } finally {
        setSubmitting(false);
      }
    },
  });
  console.log(formik.errors, "formikformikformik");

  const handleSelectChange = useCallback(
    (fieldName) => (option) => {
      if (fieldName === "method") {
        const value = option ? option.value : "";
        formik.setFieldValue("method", value);
        formik.setFieldTouched("method", false);
      } else if (fieldName === "platforms") {
        const values = option ? option.map((opt) => opt.value) : [];
        const newMeta = { ...formik.values.meta };
        // Remove URLs for deselected platforms
        Object.keys(newMeta).forEach((key) => {
          if (!values.includes(key)) {
            delete newMeta[key];
          }
        });
        formik.setFieldValue("platforms", values);
        formik.setFieldValue("meta", newMeta);
        formik.setFieldTouched("platforms", false);
      }
    },
    [formik]
  );

  const handleUrlChange = useCallback(
    (platform) => (e) => {
      const url = e.target.value;
      formik.setFieldValue("meta", {
        ...formik.values.meta,
        [platform]: url,
      });
      formik.setFieldTouched("meta", true);
    },
    [formik]
  );

  return (
    <div className="affiliate_container">
      <div className="note_section">
        Please note*: Payout amount will be determined by verified views.
      </div>
      <form>
        {/* Payment Method */}
        <label htmlFor="method" className="form_input_label">
          Payment Method
          <Select
            className="form_input_box react-select-container"
            classNamePrefix="react-select"
            name="method"
            id="method"
            placeholder="Select your Payment Method"
            styles={customStyles}
            isSearchable={false}
            options={paymentOptions}
            value={
              paymentOptions.find(
                (item) => item.value === formik.values.method
              ) || null
            }
            onChange={handleSelectChange("method")}
            onBlur={() => formik.setFieldTouched("method", true)}
            isClearable
            aria-describedby="method-error"
          />
          {formik.touched.method && formik.errors.method && (
            <div id="method-error" className="error_message">
              {formik.errors.method}
            </div>
          )}
        </label>

        {/* Platform */}
        <label htmlFor="platform" className="form_input_label">
          Platform
          <Select
            isMulti
            className="form_input_box react-select-container"
            classNamePrefix="react-select"
            name="platforms"
            id="platform"
            placeholder="Select your Platform(s)"
            styles={customStyles}
            isSearchable={false}
            options={platformOptions}
            value={platformOptions.filter((option) =>
              formik.values.platforms.includes(option.value)
            )}
            onChange={handleSelectChange("platforms")}
            onBlur={() => formik.setFieldTouched("platforms", true)}
            isClearable
            aria-describedby="platform-error"
          />
          {formik.touched.platforms && formik.errors.platforms && (
            <div id="platform-error" className="error_message">
              {formik.errors.platforms}
            </div>
          )}
        </label>

        {/* Dynamic URL Inputs for Each Platform */}
        {formik.values.platforms.map((platform) => (
          <label
            key={platform}
            htmlFor={`meta.${platform}`}
            className="form_input_label"
          >
            {platformOptions.find((opt) => opt.value === platform)?.label} URL
            <input
              type="text"
              className="form_input_box"
              id={`meta.${platform}`}
              name={`meta.${platform}`}
              placeholder={`Enter your ${platform} URL (e.g., https://${platform}.com/your_profile)`}
              onChange={handleUrlChange(platform)}
              onBlur={() => formik.setFieldTouched("meta", true)}
              value={formik.values.meta[platform] || ""}
              aria-describedby={`meta.${platform}-error`}
            />
            {formik.touched.meta &&
              formik.errors.meta &&
              !formik.values.meta[platform]?.match(/^https:\/\/.+/) && (
                <div id={`meta.${platform}-error`} className="error_message">
                  Enter a valid URL starting with https://
                </div>
              )}
          </label>
        ))}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={formik.isSubmitting}
          className="standard-button"
          style={{ minWidth: "100px" }}
          onClick={formik.handleSubmit}
        >
          {formik.isSubmitting ? <Spinner size={20} stroke={2} /> : "Submit"}
        </button>
      </form>
    </div>
  );
};

export default AlphaCreator;
